/**
 * CoherenceBar (DC-1492)
 *
 * Visual rendering of a coherence value in [0, 1]. Coherence is computed
 * per pillar from the variance across the legs whose state is
 * `present_and_scored`; 1 = perfect agreement. Null when fewer than two legs
 * contribute (variance undefined) — the bar renders an "n/a" placeholder so
 * the layout stays predictable.
 *
 * Spec: Sprint 15 design doc, "Coherence and pattern identification"
 * (lines 86–87).
 *
 * Editorial redesign (2026-05-26): repainted onto the cream/teal system via
 * scoped `.tri-coh-*` classes. data-testid / data-state / data-tier and the
 * "Coherence" / "n/a" text contract are unchanged.
 */

import React from 'react';

interface CoherenceBarProps {
  coherence: number | null;
}

const TIER_VAR: Record<'high' | 'medium' | 'low', string> = {
  high: 'var(--tri-good)',
  medium: 'var(--tri-soft)',
  low: 'var(--tri-hard)',
};

export const CoherenceBar: React.FC<CoherenceBarProps> = ({ coherence }) => {
  if (coherence === null) {
    return (
      <div data-testid="coherence-bar" data-state="na" className="tri-coherence">
        <span className="tri-coh-label">Coherence</span>
        <span className="tri-coh-val tri-coh-na">n/a</span>
        <span className="tri-coh-hint">(needs ≥2 legs)</span>
      </div>
    );
  }

  const pct = Math.max(0, Math.min(1, coherence));
  const tier = pct >= 0.8 ? 'high' : pct >= 0.5 ? 'medium' : 'low';
  const color = TIER_VAR[tier];

  return (
    <div data-testid="coherence-bar" data-state="value" data-tier={tier} className="tri-coherence">
      <span className="tri-coh-label">Coherence</span>
      <div className="tri-coh-track">
        <div className="tri-coh-fill" style={{ width: `${pct * 100}%`, background: color }} />
      </div>
      <span className="tri-coh-val" style={{ color }}>
        {(pct * 100).toFixed(0)}%
      </span>
    </div>
  );
};
